import axiosClient from '../interceptors';
import {ApiRoutes} from "../routes/ApiRoutes";
import {getAPIUrl} from "./BaseService";
import {Classroom} from "../types";

export interface PaginatedClassroomsResponse {
    items: Classroom[];
    total: number;
    skip: number;
    limit: number;
}

export interface CreateClassroomPayload {
    name: string;
    school_id: string;
    description?: string | null;
    grade_level?: number | null;
}

export interface UpdateClassroomPayload {
    name?: string;
    description?: string | null;
    grade_level?: number | null;
    rubric_id?: string | null;
    default_report_template_id?: string | null;
    default_grade_scheme_id?: string | null;
    next_classroom_id?: string | null;
    is_terminal?: boolean;
}

export async function createClassroom(
    payload: CreateClassroomPayload
): Promise<Classroom> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS;
    const response = await axiosClient.post<Classroom>(url, payload);
    return response.data;
}

export async function updateClassroomById(
    classroomId: string,
    payload: UpdateClassroomPayload
): Promise<Classroom> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId;
    const response = await axiosClient.patch<Classroom>(url, payload);
    return response.data;
}

export interface ListClassroomsParams {
    sessionId?: string;
    termId?: string;
}

export async function listClassrooms(
    skip: number = 0,
    limit: number = 50,
    params: ListClassroomsParams = {},
): Promise<PaginatedClassroomsResponse> {
    const query: Record<string, string> = {
        skip: String(skip),
        limit: String(limit),
    };
    if (params.sessionId) query.session_id = params.sessionId;
    if (params.termId) query.term_id = params.termId;
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS;
    const response = await axiosClient.get<PaginatedClassroomsResponse>(url, { params: query });
    return response.data;
}

export async function getClassroomById(classroomId: string): Promise<Classroom> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId;
    const response = await axiosClient.get<Classroom>(url);
    return response.data;
}

export async function deleteClassroomById(classroomId: string): Promise<void> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId;
    await axiosClient.delete(url);
}

export async function assignTeacherToClassroom(
    classroomId: string,
    teacherId: string,
    subjectIds?: string[]
): Promise<any> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId + '/teachers';
    const response = await axiosClient.post(url, {
        teacher_id: teacherId,
        subject_ids: subjectIds ?? null,
    });
    return response.data;
}

export async function removeTeacherFromClassroom(
    classroomId: string,
    teacherId: string
): Promise<void> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId + '/teachers/' + teacherId;
    await axiosClient.delete(url);
}

export async function updateClassroomTeacherSubjects(
    classroomId: string,
    teacherId: string,
    subjectIds: string[] | null
): Promise<any> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId + '/teachers/' + teacherId;
    const response = await axiosClient.patch(url, { subject_ids: subjectIds });
    return response.data;
}

export interface ClassroomTeacher {
    id: string;
    classroom_id: string;
    teacher_id: string;
    teacher?: {
        id: string;
        first_name: string;
        last_name: string;
    } | null;
    subject_ids: string[] | null;
    created_at: string;
}

export async function listClassroomTeachers(
    classroomId: string
): Promise<ClassroomTeacher[]> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId + '/teachers';
    const response = await axiosClient.get<ClassroomTeacher[]>(url);
    return response.data;
}

export interface Enrollment {
    id: string;
    student_id: string;
    classroom_id: string;
    term_id: string;
    status: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

interface ListEnrollmentsParams {
    studentId?: string;
    classroomId?: string;
    termId?: string;
    skip?: number;
    limit?: number;
}

export async function listEnrollments(
    params: ListEnrollmentsParams = {}
): Promise<Enrollment[]> {
    const url = getAPIUrl() + '/enrollments';
    const response = await axiosClient.get<Enrollment[]>(url, {
        params: {
            student_id: params.studentId,
            classroom_id: params.classroomId,
            term_id: params.termId,
            skip: params.skip ?? 0,
            limit: params.limit ?? 50,
        },
    });
    return response.data;
}

export async function enrollStudentInClassroom(
    studentId: string,
    classroomId: string,
    termId: string
): Promise<any> {
    const url = getAPIUrl() + '/enrollments';
    const response = await axiosClient.post(url, {
        student_id: studentId,
        classroom_id: classroomId,
        term_id: termId,
    });
    return response.data;
}

export async function deleteEnrollment(enrollmentId: string): Promise<void> {
    const url = getAPIUrl() + '/enrollments/' + enrollmentId;
    await axiosClient.delete(url);
}

export interface ClassroomStudentItem {
    student_id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    dob: string | null;
    classrooms_names: string[];
    enrollment_id: string;
    enrolled_at: string;
    status: string;
    is_active: boolean;
}

export async function listClassroomStudents(
    classroomId: string
): Promise<ClassroomStudentItem[]> {
    const url = getAPIUrl() + ApiRoutes.CLASSROOMS + '/' + classroomId + '/students';
    const response = await axiosClient.get<ClassroomStudentItem[]>(url);
    return response.data;
}

export async function addSubjectToClassroom(
    name: string,
    classroomId: string,
    subjectTags?: string[] | null,
): Promise<any> {
    const url = getAPIUrl() + '/subjects';
    const response = await axiosClient.post(url, {
        name: name,
        classroom_id: classroomId,
        ...(subjectTags && subjectTags.length > 0 ? { subject_tags: subjectTags } : {}),
    });
    return response.data;
}
